/* eslint-disable no-undef */
import React from 'react';
import ReactDOM from 'react-dom';
import { store } from 'lib';
import moment from 'moment';
import { Provider } from 'react-redux';
import { BigLayerPanelComponent } from './components/BigLayerPanelComponent';
import { PROXIMITY_DISTANCE_IN_METERS, PROXIMITY_RANGE_LIST_OBJ } from './constants';

// Static constants
const LAYER_IDS = {
  RETAILER_POINTS: 'retailerpoints',
  RETAILERS_DENSITY: 'retailers_density',
  RETAILERS_SWAMP: 'retailers_swamp',
  RETAILERS_NEAR_SCHOOLS_AZ: 'retailersnearschools_az',
  RETAILERS_NEAR_PARKS_AZ: 'retailersnearparks_az',
  RETAILERS_NEAR_RETAILERS_AZ: 'retailersnearretailers_az',
  NO_DENSITY: 'no_density',
  NO_PROXIMITY: 'no_proximity',
  RETAILERS_FAIL_RATE_BY_AGENCY: 'retailersfailratebyagency',
  RETAILERS_FAIL_RATE: 'retailersfailrate',
  NO_INSPECTION_FAILURE_RATE: 'no_inspection_failure_rate',
};

const VENUE_LAYERS = [
  'venue-v2-publicschool',
  'venue-v2-privateschool',
  'venue-v2-park',
  'venue-v2-college',
  'venue-v2-multihousing',
  'venue-v2-govbuilding',
  'venue-v2-hospital',
];

const VENUE_LABEL_LAYERS = VENUE_LAYERS.map((layer) => `${layer}-labels`);

const PROXIMITY_LAYERS = [
  LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ,
  LAYER_IDS.RETAILERS_NEAR_PARKS_AZ,
  LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ,
];

const DENSITY_LAYERS = [LAYER_IDS.RETAILERS_DENSITY, LAYER_IDS.RETAILERS_SWAMP];

const PLACE_TO_LAYER_MAP = {
  near_school: LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ,
  near_parks: LAYER_IDS.RETAILERS_NEAR_PARKS_AZ,
};

const PROXIMITY_RANGE_MAPPINGS = {
  near_school: {
    500: 'near_school_500ft',
    1000: 'near_school',
    1500: 'near_school_1500ft',
    2000: 'near_school_2000ft',
    half_mile: 'near_school_half_mile',
    mile: 'near_school_mile',
  },
  near_parks: {
    500: 'near_parks_500ft',
    1000: 'near_parks_1000ft',
    1500: 'near_parks_1500ft',
    2000: 'near_parks_2000ft',
    half_mile: 'near_parks_half_mile',
    mile: 'near_parks_mile',
  },
  near_retailer: {
    500: 'near_retailer',
    1000: 'near_retailer_1000',
    1500: 'near_retailer_1500',
    2000: 'near_retailer_2000',
    half_mile: 'near_retailer_half_mile',
    mile: 'near_retailer_mile',
  },
};

const RANGE_DISPLAY_MAP = {
  500: '500 feet',
  1000: '1,000 feet',
  1500: '1,500 feet',
  2000: '2,000 feet',
  half_mile: '1/2 mile',
  mile: '1 mile',
};

const DEMOG_LABELS = {
  pctblack: 'Black',
  pctamerind: 'Native American',
  pctashawpi: 'Asian or Pacific',
  pctwhite: 'White',
  pctotherrace: 'Other Race/Ethnicity',
  pcthisp: 'Hispanic',
  pctyouth: 'Youth',
  pctchild: 'Age 10-17',
  pctyoung: 'Age 18-24',
  mhhinc: 'Income',
  popdens: 'Population Density',
  edattain: 'Educational Attainment',
  sovulnindex: 'Social Vulnerability Index',
  lifeexpect: 'Life Expectancy',
};

const REVERSED_DEMOGRAPHICS = ['mhhinc', 'edattain', 'lifeexpect'];

const POLYTYPE_DISPLAY_MAP = {
  county: 'County',
  zip_Code: 'Zip Code',
  court_district: 'Court District',
  census_tract: 'Census Tract',
};

const LAYER_Z_INDEX = {
  'calcpoly-v2-boundary': 100,
  'calcpoly-v2-labels': 900,
  'venue-v2-publicschool-all': 320,
  'venue-v2-privateschool-all': 320,
  [LAYER_IDS.RETAILERS_DENSITY]: 200,
  [LAYER_IDS.RETAILERS_SWAMP]: 200,
  [LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ]: 401,
  [LAYER_IDS.RETAILERS_NEAR_PARKS_AZ]: 401,
  [LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ]: 401,
  [LAYER_IDS.RETAILER_POINTS]: 300,
  [LAYER_IDS.RETAILERS_FAIL_RATE_BY_AGENCY]: 200,
  [LAYER_IDS.RETAILERS_FAIL_RATE]: 200,
  retailersnearretailers: 301,
  retailersnearschools: 301,
  'venue-v2-publicschool': 320,
  'venue-v2-privateschool': 320,
  'venue-v2-park': 50,
  'venue-v2-college': 320,
  'venue-v2-multihousing': 320,
  'venue-v2-govbuilding': 320,
  'venue-v2-hospital': 320,
  'venue-v2-publicschool-labels': 900,
  'venue-v2-privateschool-labels': 900,
  'venue-v2-park-labels': 900,
  'venue-v2-college-labels': 900,
  'venue-v2-multihousing-labels': 900,
  'venue-v2-govbuilding-labels': 900,
  'venue-v2-hospital-labels': 900,
};

const IDENTIFIABLE_LAYERS = [
  LAYER_IDS.RETAILER_POINTS,
  'retailerpoints-all-single',
  'retailersnearschools',
  'retailersnearretailers',
  LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ,
  'retailersnearschools_az-all',
  LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ,
  'retailersnearretailers_az-all',
  LAYER_IDS.RETAILERS_NEAR_PARKS_AZ,
  'retailersnearparks_az-all',
  'retailersnearparksschools_500ft',
  'retailersnearparksschools_1000ft',
  'retailersnearparksschools_1500ft',
  'retailersnearparksschools_2000ft',
  'retailersnearparksschools_half_mile',
  'retailersnearparksschools_mile',
  'companyassignments',
  LAYER_IDS.RETAILERS_FAIL_RATE_BY_AGENCY,
  LAYER_IDS.RETAILERS_FAIL_RATE,
  ...VENUE_LAYERS,
  ...VENUE_LAYERS.map((layer) => `${layer}-all`),
];

const RETAILER_FILTER_MAPPING = {
  [LAYER_IDS.RETAILER_POINTS]: 'retailerpoints-all-single',
  retailersnearretailers: 'retailersnearretailers-all',
  retailersnearschools: 'retailersnearschools-all',
  [LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ]: 'retailersnearschools_az-all',
  [LAYER_IDS.RETAILERS_NEAR_PARKS_AZ]: 'retailersnearparks_az-all',
  [LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ]: 'retailersnearretailers_az-all',
};

const VENUE_FILTER_MAPPING = {
  'venue-v2-privateschool': 'venue-v2-privateschool-all',
  'venue-v2-privateschool-labels': 'venue-v2-privateschool-labels-all',
  'venue-v2-publicschool': 'venue-v2-publicschool-all',
  'venue-v2-publicschool-labels': 'venue-v2-publicschool-labels-all',
  'venue-v2-park': 'venue-v2-park-all',
  'venue-v2-park-labels': 'venue-v2-park-labels-all',
  'venue-v2-college': 'venue-v2-college-all',
  'venue-v2-college-labels': 'venue-v2-college-labels-all',
  'venue-v2-multihousing': 'venue-v2-multihousing-all',
  'venue-v2-multihousing-labels': 'venue-v2-multihousing-labels-all',
  'venue-v2-govbuilding': 'venue-v2-govbuilding-all',
  'venue-v2-govbuilding-labels': 'venue-v2-govbuilding-labels-all',
  'venue-v2-hospital': 'venue-v2-hospital-all',
  'venue-v2-hospital-labels': 'venue-v2-hospital-labels-all',
};

L.biglayerpanel = function (options) {
  return new L.Control.BigLayerPanel(options);
};

L.Control.BigLayerPanel = L.Control.extend({
  options: {
    position: 'topleft',
    token: '',
    RETAILERSETID: 0,
    VENUESETID: 0,
    calculableBoundaryLayers: [],
    controlid: null,
    subdomain: '',
    campaignId: '',
    mapType: '',
    mapFilterParams: {},
    handleMapFilters: () => {},
  },

  initialize(options) {
    L.setOptions(this, options);
    this._isExpanded = true;

    this._densitySelection = {
      retailerpoints: true,
      activeLayer: LAYER_IDS.NO_DENSITY,
      densityPolytype: 'county',
      swampPolytype: 'county',
    };

    this._proximitySelection = {
      activeLayer: LAYER_IDS.NO_PROXIMITY,
      layerConfigs: {
        near_school: {
          range: '1000',
          place: 'near_school',
        },
        near_retailer: {
          range: '1000',
        },
      },
    };
    this._inspectionFailureRateSelection = {
      activeLayer: LAYER_IDS.NO_INSPECTION_FAILURE_RATE,
      agencyId: null,
      polytype: null,
      startDate: null,
      endDate: null,
    };
  },

  onAdd(map) {
    this._map = map;
    this._container = L.DomUtil.create('div', 'leaflet-biglayerpanel-control leaflet-control');

    L.DomEvent.disableClickPropagation(this._container);
    L.DomEvent.disableScrollPropagation(this._container);

    const selectorsToDisable = [
      '.BaseSelect__control',
      '.BaseSelect__menu',
      '.BaseSelect__option',
      'button',
      'input',
      'checkbox',
    ];

    selectorsToDisable.forEach((selector) => {
      const elements = this._container.querySelectorAll(selector);
      elements.forEach((element) => {
        L.DomEvent.disableClickPropagation(element);
      });
    });

    this._setupWmsSource();
    this.setupMapClickHandling();

    this._isExpanded = true;
    this._calcPolygonSelections = [];
    this._calcPolygonLabelSelections = [];
    this._demographicSelection = null;
    this._densitySelection = {
      retailerpoints: true,
      activeLayer: LAYER_IDS.NO_DENSITY,
      densityPolytype: 'county',
      swampPolytype: 'county',
    };
    this._proximitySelection = {
      activeLayer: LAYER_IDS.NO_PROXIMITY,
      layerConfigs: {
        near_school: {
          range: '1000',
          place: 'near_school',
        },
        near_retailer: {
          range: '1000',
        },
      },
    };
    this._inspectionFailureRateSelection = {
      activeLayer: LAYER_IDS.NO_INSPECTION_FAILURE_RATE,
      agencyId: null,
      polytype: null,
      startDate: null,
      endDate: null,
    };
    this._wmssource.addSubLayer(LAYER_IDS.RETAILER_POINTS, 'noredraw');
    this._renderReactComponent();

    return this._container;
  },

  _calcPolygonSelections: [],
  _calcPolygonLabelSelections: [],
  _demographicSelection: null,
  _densitySelection: null,

  getInspectionFailureRateSelection() {
    return (
      this._inspectionFailureRateSelection || {
        activeLayer: LAYER_IDS.NO_INSPECTION_FAILURE_RATE,
        agencyId: null,
        polytype: null,
        startDate: null,
        endDate: null,
      }
    );
  },

  setInspectionFailureRateSelection(selection) {
    this._inspectionFailureRateSelection = selection;
    this._renderReactComponent();
  },

  updateInspectionFailureRateSelection(updates) {
    this._inspectionFailureRateSelection = {
      ...this.getInspectionFailureRateSelection(),
      ...updates,
    };
    this._renderReactComponent();
  },

  isInspectionFailureRateOverlayActive() {
    const inspectionFailureRateSelection = this.getInspectionFailureRateSelection();
    return (
      inspectionFailureRateSelection.activeLayer &&
      inspectionFailureRateSelection.activeLayer !== LAYER_IDS.NO_INSPECTION_FAILURE_RATE
    );
  },

  updateInspectionFailureRateOverlay(agencyId, polytype, startDate, endDate) {
    this._clearConflictingLayers();

    const layerName = agencyId ? LAYER_IDS.RETAILERS_FAIL_RATE_BY_AGENCY : LAYER_IDS.RETAILERS_FAIL_RATE;

    this.updateInspectionFailureRateSelection({
      activeLayer: layerName,
      agencyId,
      polytype,
      startDate,
      endDate,
    });
    this.setCalculablePolygonBoundaryOverlaySelections([polytype]);

    this._renderReactComponent();
    this.refresh();
  },

  clearInspectionFailureRateOverlay() {
    if (this.isInspectionFailureRateOverlayActive()) {
      if (this._wmssource && this._wmssource._subLayers) {
        Object.keys(this._wmssource._subLayers).forEach((maplayerid) => {
          if (maplayerid === LAYER_IDS.RETAILERS_FAIL_RATE_BY_AGENCY || maplayerid === LAYER_IDS.RETAILERS_FAIL_RATE) {
            this._wmssource.removeSubLayer(maplayerid, 'noredraw');
          }
        });
      }
    }

    const currentSelection = this.getInspectionFailureRateSelection();
    if (currentSelection.polytype) {
      const currentBoundarySelections = this.getCalculablePolygonBoundaryOverlaySelections();
      const updatedBoundarySelections = currentBoundarySelections.filter(
        (selection) => selection !== currentSelection.polytype,
      );
      this.setCalculablePolygonBoundaryOverlaySelections(updatedBoundarySelections);
    }

    this.updateInspectionFailureRateSelection({
      activeLayer: LAYER_IDS.NO_INSPECTION_FAILURE_RATE,
      agencyId: null,
      polytype: null,
      startDate: null,
      endDate: null,
    });

    this.refresh();
  },

  handleInspectionFailureRateMapFilter(filterData) {
    switch (filterData.type) {
      case 'inspection_failure_rate':
        this.updateInspectionFailureRateOverlay(
          filterData.agencyId,
          filterData.polytype,
          filterData.startDate,
          filterData.endDate,
        );
        break;
      case 'clear_inspection_failure_rate':
        this.clearInspectionFailureRateOverlay();
        break;
      default:
        console.warn('Unknown inspection failure rate action type:', filterData.type);
    }
  },

  _clearConflictingLayers() {
    if (this.isDensityOverlayActive()) {
      if (this._wmssource && this._wmssource._subLayers) {
        Object.keys(this._wmssource._subLayers).forEach((maplayerid) => {
          if (maplayerid === LAYER_IDS.RETAILERS_DENSITY || maplayerid === LAYER_IDS.RETAILERS_SWAMP) {
            this._wmssource.removeSubLayer(maplayerid, 'noredraw');
          }
        });
      }
      this.updateDensitySelection({
        activeLayer: LAYER_IDS.NO_DENSITY,
      });
    }

    if (this._demographicSelection) {
      if (this._wmssource && this._wmssource._subLayers) {
        Object.keys(this._wmssource._subLayers).forEach((maplayerid) => {
          if (maplayerid.indexOf('demog_v2_') === 0) {
            this._wmssource.removeSubLayer(maplayerid, 'noredraw');
          }
        });
      }
      this._demographicSelection = null;
    }
    const currentBoundarySelections = this.getCalculablePolygonBoundaryOverlaySelections();
    if (currentBoundarySelections.length > 0) {
      this.setCalculablePolygonBoundaryOverlaySelections([]);
    }
  },

  _removeAllProximityLayers(proximitySelection) {
    if (
      !proximitySelection.activeLayer ||
      proximitySelection.activeLayer === LAYER_IDS.RETAILER_POINTS ||
      proximitySelection.activeLayer === LAYER_IDS.NO_PROXIMITY
    ) {
      return;
    }

    const layersToRemove = [];
    const currentConfig = this._getCurrentLayerConfig();

    if (currentConfig && currentConfig.place) {
      if (currentConfig.place === 'near_school') {
        layersToRemove.push('venue-v2-publicschool', 'venue-v2-privateschool');
      } else if (currentConfig.place === 'near_parks') {
        layersToRemove.push('venue-v2-park');
      } else if (currentConfig.place === 'retailersnearparksschools') {
        layersToRemove.push('venue-v2-publicschool', 'venue-v2-privateschool', 'venue-v2-park');
      }
    }

    const currentLayerName = this._getProximityWmsLayerName(proximitySelection);
    if (currentLayerName && currentLayerName !== LAYER_IDS.RETAILER_POINTS) {
      layersToRemove.push(currentLayerName);
    }

    PROXIMITY_LAYERS.forEach((layerId) => {
      if (this.isLayerOnMap(layerId)) {
        layersToRemove.push(layerId);
      }
    });

    Object.keys(PROXIMITY_RANGE_LIST_OBJ.retailersnearparksschools).forEach((range) => {
      const layerName = PROXIMITY_RANGE_LIST_OBJ.retailersnearparksschools[range];
      if (this.isLayerOnMap(layerName)) {
        layersToRemove.push(layerName);
      }
    });

    layersToRemove.forEach((layerId) => {
      if (this.isLayerOnMap(layerId)) {
        this._wmssource.removeSubLayer(layerId, 'noredraw');
      }
    });
  },

  isDensityOverlayActive() {
    const densitySelection = this.getDensitySelection();
    return densitySelection.activeLayer && densitySelection.activeLayer !== LAYER_IDS.NO_DENSITY;
  },

  isProximityOverlayActive() {
    const proximitySelection = this.getProximitySelection();
    return proximitySelection.activeLayer && proximitySelection.activeLayer !== LAYER_IDS.NO_PROXIMITY;
  },

  getProximitySelection() {
    return (
      this._proximitySelection || {
        activeLayer: LAYER_IDS.NO_PROXIMITY,
        layerConfigs: {
          near_school: {
            range: '1000',
            place: 'near_school',
          },
          near_retailer: {
            range: '1000',
          },
        },
      }
    );
  },

  setProximitySelection(selection) {
    this._proximitySelection = selection;
    this._renderReactComponent();
  },

  updateProximitySelection(updates) {
    this._proximitySelection = {
      ...this.getProximitySelection(),
      ...updates,
    };
    this._renderReactComponent();
  },

  updateProximityLayer(layerId, isChecked, range, place) {
    if (layerId === LAYER_IDS.RETAILER_POINTS) {
      this.toggleRetailerPoints(isChecked);
      return;
    }

    if (layerId === LAYER_IDS.NO_PROXIMITY) {
      if (isChecked) {
        const currentSelection = this.getProximitySelection();
        if (this.isProximityOverlayActive()) {
          this._removeAllProximityLayers(currentSelection);
        }
        this.updateProximitySelection({
          activeLayer: LAYER_IDS.NO_PROXIMITY,
        });
      }
      this.refresh();
      return;
    }

    if (!isChecked) {
      const currentSelection = this.getProximitySelection();
      if (currentSelection.activeLayer === layerId) {
        this._removeAllProximityLayers(currentSelection);
        this.updateProximitySelection({
          activeLayer: LAYER_IDS.NO_PROXIMITY,
        });
      }
    } else {
      const currentSelection = this.getProximitySelection();

      if (this.isRetailerPointsActive()) {
        this.updateDensitySelection({
          retailerpoints: false,
        });
        this._wmssource.removeSubLayer(LAYER_IDS.RETAILER_POINTS, 'noredraw');
      }

      if (this.isProximityOverlayActive()) {
        this._removeAllProximityLayers(currentSelection);
      }

      const configType = this._getLayerConfigType(layerId);
      const updates = { activeLayer: layerId };

      if (configType && currentSelection.layerConfigs[configType]) {
        const existingConfig = currentSelection.layerConfigs[configType];
        const newConfig = { ...existingConfig };

        if (range !== undefined) {
          newConfig.range = range;
        }
        if (place !== undefined && configType === 'near_school') {
          newConfig.place = place;
        }

        updates.layerConfigs = {
          ...currentSelection.layerConfigs,
          [configType]: newConfig,
        };
      }

      this.updateProximitySelection(updates);
    }

    this.refresh();
  },

  updateProximityRange(range) {
    const currentSelection = this.getProximitySelection();
    const configType = this._getLayerConfigType(currentSelection.activeLayer);

    if (!configType) return;

    if (this.isProximityOverlayActive() && currentSelection.activeLayer !== LAYER_IDS.RETAILER_POINTS) {
      this._removeProximityReferenceLayers(currentSelection);
    }

    this.updateProximitySelection({
      layerConfigs: {
        ...currentSelection.layerConfigs,
        [configType]: {
          ...currentSelection.layerConfigs[configType],
          range,
        },
      },
    });
    this.refresh();
  },

  updateProximityPlace(place) {
    const currentSelection = this.getProximitySelection();
    const configType = this._getLayerConfigType(currentSelection.activeLayer);

    if (configType !== 'near_school') return;

    if (this.isProximityOverlayActive() && currentSelection.activeLayer !== LAYER_IDS.RETAILER_POINTS) {
      this._removeProximityReferenceLayers(currentSelection);
    }

    this.updateProximitySelection({
      layerConfigs: {
        ...currentSelection.layerConfigs,
        [configType]: {
          ...currentSelection.layerConfigs[configType],
          place,
        },
      },
    });
    this.refresh();
  },

  isProximityLayerActive(layerId) {
    const proximitySelection = this.getProximitySelection();

    if (layerId === LAYER_IDS.RETAILER_POINTS) {
      return proximitySelection.activeLayer === LAYER_IDS.RETAILER_POINTS;
    }

    if (layerId === LAYER_IDS.NO_PROXIMITY) {
      return proximitySelection.activeLayer === LAYER_IDS.NO_PROXIMITY;
    }

    if (PROXIMITY_LAYERS.includes(layerId)) {
      return proximitySelection.activeLayer === layerId;
    }

    if (layerId === LAYER_IDS.RETAILERS_NEAR_PARKS_AZ) {
      return (
        proximitySelection.activeLayer === LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ &&
        proximitySelection.place === 'near_parks'
      );
    }

    if (layerId.indexOf('retailersnearparksschools_') === 0) {
      const currentLayerName = this._getProximityWmsLayerName(proximitySelection);
      return currentLayerName === layerId;
    }

    return false;
  },

  getProximityLegendInfo() {
    const proximitySelection = this.getProximitySelection();

    if (!proximitySelection.activeLayer || proximitySelection.activeLayer === LAYER_IDS.NO_PROXIMITY) {
      return null;
    }

    const currentConfig = this._getCurrentLayerConfig();
    if (!currentConfig) return null;

    const layerInfo = {
      [LAYER_IDS.RETAILER_POINTS]: {
        title: 'Retailer Locations',
        swatch: { type: 'circle', color: '#FF8080', image: null },
      },
      [LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ]: {
        title: 'Retailers Near Schools/Parks',
        areaname: `within ${this._formatRangeDisplay(currentConfig.range)}`,
        swatch: { type: 'circle', color: '#913fdf', image: null },
      },
      [LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ]: {
        title: 'Retailers Near Other Retailers',
        areaname: `within ${this._formatRangeDisplay(currentConfig.range)}`,
        swatch: { type: 'circle', color: '#913fdf', image: null },
      },
    };

    if (currentConfig.place === 'retailersnearparksschools') {
      return {
        title: 'Retailers Near Parks or Schools',
        areaname: `within ${this._formatRangeDisplay(currentConfig.range)}`,
        swatch: { type: 'circle', color: '#913fdf', image: null },
      };
    }

    return layerInfo[proximitySelection.activeLayer] || null;
  },

  _clearOnlyProximityLayers() {
    const proximitySelection = this.getProximitySelection();

    if (this.isProximityOverlayActive()) {
      this._removeProximityReferenceLayers(proximitySelection);
      this.updateProximitySelection({
        activeLayer: LAYER_IDS.NO_PROXIMITY,
      });
    }
  },

  _getProximityWmsLayerName(proximitySelection) {
    if (!proximitySelection.activeLayer || proximitySelection.activeLayer === LAYER_IDS.RETAILER_POINTS) {
      return proximitySelection.activeLayer;
    }

    const currentConfig = this._getCurrentLayerConfig();
    if (!currentConfig) return proximitySelection.activeLayer;

    if (currentConfig.place === 'retailersnearparksschools') {
      const proximityRangeObj = PROXIMITY_RANGE_LIST_OBJ.retailersnearparksschools;
      return proximityRangeObj[currentConfig.range];
    }

    if (proximitySelection.activeLayer === LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ) {
      return PLACE_TO_LAYER_MAP[currentConfig.place] || proximitySelection.activeLayer;
    }

    return proximitySelection.activeLayer;
  },

  _getProximityDistanceParameter(proximitySelection) {
    if (!proximitySelection.activeLayer || proximitySelection.activeLayer === LAYER_IDS.RETAILER_POINTS) {
      return '';
    }

    const currentConfig = this._getCurrentLayerConfig();
    if (!currentConfig) return '';

    if (currentConfig.place === 'retailersnearparksschools') {
      return '';
    }

    if (proximitySelection.activeLayer === LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ) {
      return PROXIMITY_RANGE_MAPPINGS.near_retailer[currentConfig.range] || '';
    }

    if (proximitySelection.activeLayer === LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ) {
      return PROXIMITY_RANGE_MAPPINGS[currentConfig.place][currentConfig.range] || '';
    }

    return '';
  },

  _getLayerConfigType(layerId) {
    if (layerId === LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ) {
      return 'near_retailer';
    }
    if (
      [LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ, LAYER_IDS.RETAILERS_NEAR_PARKS_AZ].includes(layerId) ||
      layerId === 'retailersnearparksschools'
    ) {
      return 'near_school';
    }
    return null;
  },

  _getCurrentLayerConfig() {
    const proximitySelection = this.getProximitySelection();
    const configType = this._getLayerConfigType(proximitySelection.activeLayer);
    if (!configType) return null;
    return proximitySelection.layerConfigs[configType];
  },

  _formatRangeDisplay(range) {
    return RANGE_DISPLAY_MAP[range] || range;
  },

  getDensitySelection() {
    return (
      this._densitySelection || {
        retailerpoints: true,
        activeLayer: LAYER_IDS.NO_DENSITY,
        densityPolytype: 'county',
        swampPolytype: 'county',
      }
    );
  },

  setDensitySelection(selection) {
    this._densitySelection = selection;
    this._renderReactComponent();
  },

  updateDensitySelection(updates) {
    this._densitySelection = {
      ...this.getDensitySelection(),
      ...updates,
    };
    this._renderReactComponent();
  },

  updateRetailerPointsLayer(isChecked) {
    this.toggleRetailerPoints(isChecked);
  },

  updateDensityOverlay(radioSelection, polytype) {
    const currentSelection = this.getDensitySelection();

    if (radioSelection === LAYER_IDS.NO_DENSITY) {
      if (this.isDensityOverlayActive()) {
        if (this._wmssource && this._wmssource._subLayers) {
          Object.keys(this._wmssource._subLayers).forEach((maplayerid) => {
            if (maplayerid === LAYER_IDS.RETAILERS_DENSITY || maplayerid === LAYER_IDS.RETAILERS_SWAMP) {
              this._wmssource.removeSubLayer(maplayerid, 'noredraw');
            }
          });
        }
      }

      this.updateDensitySelection({
        activeLayer: LAYER_IDS.NO_DENSITY,
      });
      this.refresh();
      return;
    }

    if (radioSelection && radioSelection !== LAYER_IDS.NO_DENSITY && this._demographicSelection) {
      this._demographicSelection = null;

      if (this._wmssource && this._wmssource._subLayers) {
        Object.keys(this._wmssource._subLayers).forEach((maplayerid) => {
          if (maplayerid.indexOf('demog_v2_') === 0) {
            this._wmssource.removeSubLayer(maplayerid, 'noredraw');
          }
        });
      }
    }

    if (radioSelection && radioSelection !== LAYER_IDS.NO_DENSITY) {
      if (this.isInspectionFailureRateOverlayActive()) {
        if (this._wmssource && this._wmssource._subLayers) {
          Object.keys(this._wmssource._subLayers).forEach((maplayerid) => {
            if (
              maplayerid === LAYER_IDS.RETAILERS_FAIL_RATE_BY_AGENCY ||
              maplayerid === LAYER_IDS.RETAILERS_FAIL_RATE
            ) {
              this._wmssource.removeSubLayer(maplayerid, 'noredraw');
            }
          });
        }
        if (currentSelection.polytype) {
          const currentBoundarySelections = this.getCalculablePolygonBoundaryOverlaySelections();
          const updatedBoundarySelections = currentBoundarySelections.filter(
            (selection) => selection !== currentSelection.polytype,
          );
          this.setCalculablePolygonBoundaryOverlaySelections(updatedBoundarySelections);
        }
        this.updateInspectionFailureRateSelection({
          activeLayer: LAYER_IDS.NO_INSPECTION_FAILURE_RATE,
          agencyId: null,
          polytype: null,
          startDate: null,
          endDate: null,
        });
      }
    }

    const updates = { activeLayer: radioSelection };

    if (radioSelection === LAYER_IDS.RETAILERS_DENSITY) {
      updates.densityPolytype = polytype || currentSelection.densityPolytype;
    } else if (radioSelection === LAYER_IDS.RETAILERS_SWAMP) {
      updates.swampPolytype = polytype || currentSelection.swampPolytype;
    }

    this.updateDensitySelection(updates);

    if (this._wmssource && this._wmssource._subLayers) {
      Object.keys(this._wmssource._subLayers).forEach((maplayerid) => {
        if (maplayerid === LAYER_IDS.RETAILERS_DENSITY || maplayerid === LAYER_IDS.RETAILERS_SWAMP) {
          this._wmssource.removeSubLayer(maplayerid, 'noredraw');
        }
      });
    }

    this.refresh();
  },

  getCalculablePolygonBoundaryOverlaySelections() {
    return this._calcPolygonSelections || [];
  },

  getCalculablePolygonLabelOverlaySelections() {
    return this._calcPolygonLabelSelections || [];
  },

  setCalculablePolygonBoundaryOverlaySelections(selections) {
    this._calcPolygonSelections = selections;
    this._renderReactComponent();
  },

  setCalculablePolygonLabelOverlaySelections(selections) {
    this._calcPolygonLabelSelections = selections;
    this._renderReactComponent();
  },

  updateCalculablePolygonBoundaryOverlay(layerId, isChecked) {
    if (!this._calcPolygonSelections) {
      this._calcPolygonSelections = [];
    }

    if (isChecked) {
      if (!this._calcPolygonSelections.includes(layerId)) {
        this._calcPolygonSelections.push(layerId);
      }
    } else {
      this._calcPolygonSelections = this._calcPolygonSelections.filter((id) => id !== layerId);
    }

    this.refresh();
  },

  updateCalculablePolygonLabelOverlay(layerId, isChecked) {
    if (!this._calcPolygonLabelSelections) {
      this._calcPolygonLabelSelections = [];
    }

    if (isChecked) {
      if (!this._calcPolygonLabelSelections.includes(layerId)) {
        this._calcPolygonLabelSelections.push(layerId);
      }
    } else {
      this._calcPolygonLabelSelections = this._calcPolygonLabelSelections.filter((id) => id !== layerId);
    }

    this.refresh();
  },

  getDemographicSelection() {
    return this._demographicSelection || null;
  },

  setDemographicSelection(selection) {
    this._demographicSelection = selection;
    this.refresh();
  },

  updateDemographicOverlay(polytype, demoglayer) {
    if (!polytype && !demoglayer) {
      this._demographicSelection = null;
    } else {
      if (this._densitySelection && this._densitySelection.activeLayer) {
        this.updateDensitySelection({
          activeLayer: null,
        });

        if (this._wmssource && this._wmssource._subLayers) {
          Object.keys(this._wmssource._subLayers).forEach((maplayerid) => {
            if (maplayerid === LAYER_IDS.RETAILERS_DENSITY || maplayerid === LAYER_IDS.RETAILERS_SWAMP) {
              this._wmssource.removeSubLayer(maplayerid, 'noredraw');
            }
          });
        }
      }

      this._demographicSelection = {
        polytype,
        demoglayer,
      };
    }

    if (this.isInspectionFailureRateOverlayActive()) {
      const currentSelection = this.getInspectionFailureRateSelection();
      if (this._wmssource && this._wmssource._subLayers) {
        Object.keys(this._wmssource._subLayers).forEach((maplayerid) => {
          if (maplayerid === LAYER_IDS.RETAILERS_FAIL_RATE_BY_AGENCY || maplayerid === LAYER_IDS.RETAILERS_FAIL_RATE) {
            this._wmssource.removeSubLayer(maplayerid, 'noredraw');
          }
        });
      }
      if (currentSelection.polytype) {
        const currentBoundarySelections = this.getCalculablePolygonBoundaryOverlaySelections();
        const updatedBoundarySelections = currentBoundarySelections.filter(
          (selection) => selection !== currentSelection.polytype,
        );
        this.setCalculablePolygonBoundaryOverlaySelections(updatedBoundarySelections);
      }
      this.updateInspectionFailureRateSelection({
        activeLayer: LAYER_IDS.NO_INSPECTION_FAILURE_RATE,
        agencyId: null,
        polytype: null,
        startDate: null,
        endDate: null,
      });
    }

    this.refresh();
  },

  getCurrentDemographicLegendInfo() {
    const demogchoice = this.getDemographicSelection();
    return demogchoice ? this.getDemographicLegendInfo(demogchoice.demoglayer, demogchoice.polytype) : null;
  },

  getDemographicLegendInfo(whichdemog, polytype) {
    const isReversed = REVERSED_DEMOGRAPHICS.includes(whichdemog);
    const colors = ['#f7fbff', '#deebf7', '#c6dbef', '#9ecae1', '#3182bd'];

    const polytypeMap = {};
    this.options.calculableBoundaryLayers.forEach((layer) => {
      polytypeMap[layer.slug] = layer.singular;
    });

    return {
      title: DEMOG_LABELS[whichdemog] || whichdemog,
      areaname: polytype ? `by ${polytypeMap[polytype] || polytype}` : '',
      colors,
      minlabel: isReversed ? 'Highest' : 'Lowest',
      maxlabel: isReversed ? 'Lowest' : 'Highest',
    };
  },

  getDemogFormatString(demogtype, value) {
    let text = value;
    switch (demogtype) {
      case 'mhhinc':
        text = `$${parseInt(value, 10).toLocaleString()}`;
        break;
      case 'pctblack':
      case 'pctamerind':
      case 'pctashawpi':
      case 'pctwhite':
      case 'pctotherrace':
      case 'pcthisp':
      case 'pctchild':
      case 'pctyoung':
      case 'pctyouth':
        text = `${parseFloat(value).toFixed(1)}%`;
        break;
      default:
        break;
    }
    return text;
  },

  _renderReactComponent() {
    ReactDOM.render(
      <Provider store={store}>
        <BigLayerPanelComponent
          isExpanded={this._isExpanded}
          onToggle={this.togglePanel.bind(this)}
          mapLegendImages={this.options.maplegendimages}
          calculableBoundaryLayers={this.options.calculableBoundaryLayers}
          collapsedArrowIcon={this.options.collapsedArrowIcon}
          expandedArrowIcon={this.options.expandedArrowIcon}
          activeLayerIds={this.listActiveLayerIds()}
          onLayerToggle={this.toggleLayer.bind(this)}
          calcPolygonSelections={this.getCalculablePolygonBoundaryOverlaySelections()}
          calcPolygonLabelSelections={this.getCalculablePolygonLabelOverlaySelections()}
          onCalculablePolygonChange={this.updateCalculablePolygonBoundaryOverlay.bind(this)}
          onCalculablePolygonLabelChange={this.updateCalculablePolygonLabelOverlay.bind(this)}
          demographicSelection={this.getDemographicSelection()}
          onDemographicChange={this.updateDemographicOverlay.bind(this)}
          densitySelection={this.getDensitySelection()}
          onDensityChange={this.updateDensityOverlay.bind(this)}
          onRetailerPointsToggle={this.updateRetailerPointsLayer.bind(this)}
          proximitySelection={this.getProximitySelection()}
          onProximityLayerToggle={this.updateProximityLayer.bind(this)}
          onProximityRangeChange={this.updateProximityRange.bind(this)}
          onProximityPlaceChange={this.updateProximityPlace.bind(this)}
          handleMapFilters={this.options.handleMapFilters}
          mapFilterParams={this.options.mapFilterParams}
          mapType={this.options.mapType}
          inspectionFailureRateSelection={this.getInspectionFailureRateSelection()}
          onInspectionFailureRateMapFilter={this.handleInspectionFailureRateMapFilter.bind(this)}
          campaignId={this.options.campaignId}
        />
      </Provider>,
      this._container,
    );
  },

  updateMapFilterParams(newMapFilterParams) {
    this.options.mapFilterParams = newMapFilterParams;

    this._renderReactComponent();
  },

  listAllLayerIds() {
    return [
      LAYER_IDS.RETAILER_POINTS,
      LAYER_IDS.RETAILERS_DENSITY,
      LAYER_IDS.RETAILERS_SWAMP,
      'retailersnearretailers',
      'retailersnearschools',
      LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ,
      LAYER_IDS.RETAILERS_NEAR_PARKS_AZ,
      LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ,
      LAYER_IDS.NO_DENSITY,
      LAYER_IDS.NO_PROXIMITY,
      LAYER_IDS.RETAILERS_FAIL_RATE_BY_AGENCY,
      LAYER_IDS.RETAILERS_FAIL_RATE,
      LAYER_IDS.NO_INSPECTION_FAILURE_RATE,
      'retailersnearparksschools_500ft',
      'retailersnearparksschools_1000ft',
      'retailersnearparksschools_1500ft',
      'retailersnearparksschools_2000ft',
      'retailersnearparksschools_half_mile',
      'retailersnearparksschools_mile',
      ...VENUE_LAYERS,
      ...VENUE_LABEL_LAYERS,
    ];
  },

  isLayerOnMap(maplayerid) {
    return Boolean(this._wmssource._subLayers[maplayerid]);
  },

  listActiveLayerIds() {
    const regularLayers = this.listAllLayerIds().filter((maplayerid) => this.isLayerOnMap(maplayerid));
    const densitySelection = this.getDensitySelection();
    const proximitySelection = this.getProximitySelection();
    const inspectionFailureRateSelection = this.getInspectionFailureRateSelection();
    const allLayers = [];

    if (this.isRetailerPointsActive()) {
      allLayers.push(LAYER_IDS.RETAILER_POINTS);
    }

    if (densitySelection.activeLayer && densitySelection.activeLayer !== LAYER_IDS.NO_DENSITY) {
      if (densitySelection.activeLayer === LAYER_IDS.RETAILERS_DENSITY) {
        allLayers.push(LAYER_IDS.RETAILERS_DENSITY);
      } else if (densitySelection.activeLayer === LAYER_IDS.RETAILERS_SWAMP) {
        allLayers.push(LAYER_IDS.RETAILERS_SWAMP);
      }
    }

    if (
      inspectionFailureRateSelection.activeLayer &&
      inspectionFailureRateSelection.activeLayer !== LAYER_IDS.NO_INSPECTION_FAILURE_RATE &&
      !this.isDensityOverlayActive()
    ) {
      allLayers.push(inspectionFailureRateSelection.activeLayer);
    }

    if (proximitySelection.activeLayer && proximitySelection.activeLayer !== LAYER_IDS.NO_PROXIMITY) {
      if (proximitySelection.activeLayer === LAYER_IDS.RETAILER_POINTS) {
        // Already handled above
      } else if (proximitySelection.place === 'retailersnearparksschools') {
        const proximityRangeObj = {
          retailersnearparksschools: {
            500: 'retailersnearparksschools_500ft',
            1000: 'retailersnearparksschools_1000ft',
            1500: 'retailersnearparksschools_1500ft',
            2000: 'retailersnearparksschools_2000ft',
            half_mile: 'retailersnearparksschools_half_mile',
            mile: 'retailersnearparksschools_mile',
          },
        };
        const layerName = proximityRangeObj.retailersnearparksschools[proximitySelection.range];
        if (layerName) {
          allLayers.push(layerName);
        }
      } else {
        const actualLayerName = this._getProximityWmsLayerName(proximitySelection);
        if (actualLayerName && actualLayerName !== LAYER_IDS.RETAILER_POINTS) {
          allLayers.push(actualLayerName);
        }
      }

      if (proximitySelection.activeLayer !== LAYER_IDS.RETAILER_POINTS) {
        const currentConfig = this._getCurrentLayerConfig();
        if (currentConfig && currentConfig.place) {
          if (currentConfig.place === 'near_school') {
            allLayers.push('venue-v2-publicschool', 'venue-v2-privateschool');
          } else if (currentConfig.place === 'near_parks') {
            allLayers.push('venue-v2-park');
          } else if (currentConfig.place === 'retailersnearparksschools') {
            allLayers.push('venue-v2-publicschool', 'venue-v2-privateschool', 'venue-v2-park');
          }
        }
      }
    }

    const allActiveLayers = [...new Set([...regularLayers, ...allLayers])];
    return allActiveLayers;
  },

  getLayerZIndex(layerid) {
    if (layerid.indexOf('retailersnearparksschools_') === 0) return 401;
    if (layerid.indexOf('demog_v2_') === 0) return 200;
    return LAYER_Z_INDEX[layerid] || null;
  },

  activeLayerLegends() {
    const activeLayers = this.listActiveLayerIds();
    const legendInfos = [];

    const layerInfo = {
      [LAYER_IDS.RETAILER_POINTS]: {
        title: 'Retailer Locations',
        swatch: { type: 'circle', color: '#FF8080', image: null },
      },
      [LAYER_IDS.RETAILERS_DENSITY]: {
        title: 'Retailer Density',
        swatch: { type: 'square', color: '#FF0000', image: null },
      },
      [LAYER_IDS.RETAILERS_SWAMP]: {
        title: 'Retailer Swamp Areas',
        swatch: { type: 'square', color: '#8B4513', image: null },
      },
      [LAYER_IDS.RETAILERS_FAIL_RATE_BY_AGENCY]: {
        title: 'Inspection Failure Rate by Agency',
        swatch: { type: 'square', color: '#FF6B35', image: null },
      },
      [LAYER_IDS.RETAILERS_FAIL_RATE]: {
        title: 'Inspection Failure Rate',
        swatch: { type: 'square', color: '#FF6B35', image: null },
      },
      retailersnearretailers: {
        title: 'Retailers Near Other Retailers',
        swatch: { type: 'circle', color: '#913fdf', image: null },
      },
      retailersnearschools: {
        title: 'Retailers Near Youth',
        swatch: { type: 'circle', color: '#913fdf', image: null },
      },
      [LAYER_IDS.RETAILERS_NEAR_SCHOOLS_AZ]: {
        title: 'Retailers Near Schools/Parks',
        swatch: { type: 'circle', color: '#913fdf', image: null },
      },
      [LAYER_IDS.RETAILERS_NEAR_PARKS_AZ]: {
        title: 'Retailers Near Parks',
        swatch: { type: 'circle', color: '#913fdf', image: null },
      },
      [LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ]: {
        title: 'Retailers Near Other Retailers',
        swatch: { type: 'circle', color: '#913fdf', image: null },
      },
      retailersnearparksschools_500ft: {
        title: 'Retailers Near Parks or Schools (500 ft)',
        swatch: { type: 'circle', color: '#913fdf', image: null },
      },
      retailersnearparksschools_1000ft: {
        title: 'Retailers Near Parks or Schools (1000 ft)',
        swatch: { type: 'circle', color: '#913fdf', image: null },
      },
    };

    activeLayers.forEach((layerId) => {
      if (layerId.startsWith('no_') || layerId.startsWith('venue-v2-')) {
        return;
      }

      if (layerInfo[layerId]) {
        legendInfos.push(layerInfo[layerId]);
      } else if (layerId.startsWith('retailersnearparksschools_')) {
        const proximitySelection = this.getProximitySelection();
        const rangeDisplay = this._formatRangeDisplay(proximitySelection.range);
        legendInfos.push({
          title: `Retailers Near Parks or Schools (${rangeDisplay})`,
          swatch: { type: 'circle', color: '#913fdf', image: null },
        });
      }
    });

    return legendInfos;
  },

  getCurrentDensityLegendInfo() {
    const densitySelection = this.getDensitySelection();

    if (!densitySelection.activeLayer || densitySelection.activeLayer === LAYER_IDS.NO_DENSITY) {
      return null;
    }

    const layerInfo = {
      [LAYER_IDS.RETAILERS_DENSITY]: {
        title: 'Retailer Density',
        areaname: `by ${this._getPolytypeDisplayName(densitySelection.densityPolytype)}`,
        type: 'density',
      },
      [LAYER_IDS.RETAILERS_SWAMP]: {
        title: 'Retailer Swamp Areas',
        areaname: `by ${this._getPolytypeDisplayName(densitySelection.swampPolytype)}`,
        type: 'swamp',
      },
    };

    return layerInfo[densitySelection.activeLayer] || null;
  },

  _getPolytypeDisplayName(polytype) {
    return POLYTYPE_DISPLAY_MAP[polytype] || polytype;
  },

  _removeProximityReferenceLayers(proximitySelection) {
    if (
      !proximitySelection.activeLayer ||
      proximitySelection.activeLayer === LAYER_IDS.RETAILER_POINTS ||
      proximitySelection.activeLayer === LAYER_IDS.NO_PROXIMITY
    ) {
      return;
    }

    const layersToRemove = [];
    const currentConfig = this._getCurrentLayerConfig();

    if (currentConfig && currentConfig.place) {
      if (currentConfig.place === 'near_school') {
        layersToRemove.push('venue-v2-publicschool', 'venue-v2-privateschool');
      } else if (currentConfig.place === 'near_parks') {
        layersToRemove.push('venue-v2-park');
      } else if (currentConfig.place === 'retailersnearparksschools') {
        layersToRemove.push('venue-v2-publicschool', 'venue-v2-privateschool', 'venue-v2-park');
      }
    }

    const currentLayerName = this._getProximityWmsLayerName(proximitySelection);
    if (currentLayerName && currentLayerName !== LAYER_IDS.RETAILER_POINTS && this.isLayerOnMap(currentLayerName)) {
      layersToRemove.push(currentLayerName);
    }

    layersToRemove.forEach((layerId) => {
      if (this.isLayerOnMap(layerId)) {
        this._wmssource.removeSubLayer(layerId, 'noredraw');
      }
    });
  },

  _updateUIState() {
    this._renderReactComponent();
  },

  toggleLayer(maplayerid, showit) {
    if (maplayerid === LAYER_IDS.RETAILER_POINTS) {
      this.toggleRetailerPoints(showit);
      return;
    }

    if (maplayerid === LAYER_IDS.NO_DENSITY) {
      if (showit) {
        this.updateDensityOverlay(LAYER_IDS.NO_DENSITY);
      }
      return;
    }

    if (maplayerid === LAYER_IDS.NO_PROXIMITY) {
      if (showit) {
        this.updateProximityLayer(LAYER_IDS.NO_PROXIMITY, true);
      }
      return;
    }

    const proximitySelection = this.getProximitySelection();
    const isProximityLayer = PROXIMITY_LAYERS.includes(maplayerid);

    if (isProximityLayer) {
      if (showit) {
        if (this.isProximityOverlayActive() && proximitySelection.activeLayer !== maplayerid) {
          this._removeProximityReferenceLayers(proximitySelection);
        }
        this.updateProximityLayer(maplayerid, true, proximitySelection.range, proximitySelection.place);
      } else {
        this.updateProximityLayer(maplayerid, false);
      }
      return;
    }

    const isDensityLayer = DENSITY_LAYERS.includes(maplayerid);
    if (isDensityLayer) {
      if (showit) {
        const currentSelection = this.getDensitySelection();
        let polytype = currentSelection.densityPolytype;
        if (maplayerid === LAYER_IDS.RETAILERS_SWAMP) {
          polytype = currentSelection.swampPolytype;
        }
        this.updateDensityOverlay(maplayerid, polytype);
      } else {
        this.updateDensityOverlay(LAYER_IDS.NO_DENSITY);
      }
      return;
    }

    if (showit) this._wmssource.addSubLayer(maplayerid);
    else this._wmssource.removeSubLayer(maplayerid);

    if (maplayerid === 'retailersnearschools') {
      if (showit) {
        if (!this.isLayerOnMap('venue-v2-publicschool')) this.toggleLayer('venue-v2-publicschool', true);
        if (!this.isLayerOnMap('venue-v2-privateschool')) this.toggleLayer('venue-v2-privateschool', true);
      } else if (this.options.VENUESETID === 0) {
        if (this.isLayerOnMap('venue-v2-publicschool')) this.toggleLayer('venue-v2-publicschool', false);
        if (this.isLayerOnMap('venue-v2-privateschool')) this.toggleLayer('venue-v2-privateschool', false);
      }
    }

    const retailerlayers = ['retailersnearretailers', 'retailersnearschools'];
    if (showit && retailerlayers.indexOf(maplayerid) !== -1) {
      retailerlayers.filter((id) => id !== maplayerid).forEach((id) => this.toggleLayer(id, false));
    }

    this.refresh();
  },

  isRetailerPointsActive() {
    const densitySelection = this.getDensitySelection();
    const proximitySelection = this.getProximitySelection();

    if (proximitySelection.activeLayer === LAYER_IDS.RETAILER_POINTS) {
      return true;
    }

    if (this.isProximityOverlayActive() && proximitySelection.activeLayer !== LAYER_IDS.RETAILER_POINTS) {
      return false;
    }

    return densitySelection.retailerpoints;
  },

  toggleRetailerPoints(isChecked) {
    const proximitySelection = this.getProximitySelection();

    if (isChecked) {
      if (this.isProximityOverlayActive() && proximitySelection.activeLayer !== LAYER_IDS.RETAILER_POINTS) {
        this._removeProximityReferenceLayers(proximitySelection);
        this.updateProximitySelection({
          activeLayer: LAYER_IDS.NO_PROXIMITY,
        });
      }

      this.updateDensitySelection({
        retailerpoints: true,
      });

      this._wmssource.addSubLayer(LAYER_IDS.RETAILER_POINTS);
    } else {
      this.updateDensitySelection({
        retailerpoints: false,
      });

      this._wmssource.removeSubLayer(LAYER_IDS.RETAILER_POINTS);
    }

    this._renderReactComponent();
  },

  setRetailerFilterSetId(filterid) {
    this.options.RETAILERSETID = filterid;
    this.refresh();
  },

  setVenueFilterSetId(filterid) {
    this.options.VENUESETID = filterid;
    this.refresh();
  },

  collapsePanel() {
    this._isExpanded = false;
    this._renderReactComponent();
  },

  expandPanel() {
    this._isExpanded = true;
    this._renderReactComponent();
  },

  togglePanel() {
    this._isExpanded = !this._isExpanded;
    this._renderReactComponent();
  },

  panelExpanded() {
    return this._isExpanded;
  },

  _setupWmsSource() {
    this._wmssource = L.WMS.source(this.options.wmsbaseurl, {
      version: '1.1.1',
      styles: '',
      format: 'image/png',
      transparent: 'true',
      identify: true,
      feature_count: 5,
      info_format: 'application/vnd.ogc.gml',
    }).addTo(this._map);

    this._wmssource.getLayer('universe').addTo(this._map);
    this._setupWmsFeatureInfo();
    this._extendWmsSource();
  },

  _setupWmsFeatureInfo() {
    const { getfeatureinfourl, mapType } = this.options;

    this._wmssource.getFeatureInfo = function (point, latlng, layers, callback) {
      const params = this.getFeatureInfoParams(point, layers);
      const url = getfeatureinfourl + L.Util.getParamString(params, getfeatureinfourl);

      this.showWaiting();
      this.ajax(url, done);

      function done(result) {
        this.hideWaiting();
        const text = this.parseFeatureInfo(result, url);
        callback.call(this, latlng, text);
      }
    };

    this._wmssource.ajax = function (requestUrl, callback) {
      const token = localStorage.getItem('Authorization');
      doxhr.call(this, requestUrl, token, callback);

      function doxhr(url, authToken, callbackFn) {
        const context = this;
        const request = new XMLHttpRequest();
        request.onreadystatechange = function () {
          if (request.readyState === 4) {
            if (request.status === 200) callbackFn.call(context, request.responseText);
            else callbackFn.call(context, 'error');
          }
        };
        request.open('GET', url);

        if (mapType !== '/retailers-public') request.setRequestHeader('Authorization', `Token ${authToken}`);
        request.send();
      }
    };

    this._wmssource.showFeatureInfo = (latlng, xmlstring) => {
      this.handleWmsGetFeatureInfoResults(latlng, xmlstring);
    };
  },

  _extendWmsSource() {
    const control = this;

    this._wmssource.refreshOverlay = function () {
      if (!this._map) return;

      let wmslayerids = Object.keys(this._subLayers);
      const calcpolyboundschoices = control.getCalculablePolygonBoundaryOverlaySelections();
      const calcpolylabelschoices = control.getCalculablePolygonLabelOverlaySelections();

      if (calcpolyboundschoices.length || control.isInspectionFailureRateOverlayActive()) {
        wmslayerids.push('calcpoly-v2-boundary');
      }
      if (calcpolylabelschoices.length) {
        wmslayerids.push('calcpoly-v2-labels');
      }

      const demogchoice = control.getDemographicSelection();
      const densitychoice = control.getDensitySelection();
      const proximitychoice = control.getProximitySelection();
      const inspectionFailureRatechoice = control.getInspectionFailureRateSelection();

      if (
        demogchoice &&
        demogchoice.polytype &&
        demogchoice.demoglayer &&
        !control.isDensityOverlayActive() &&
        !control.isInspectionFailureRateOverlayActive()
      ) {
        const demoglayerid = `demog_v2_${demogchoice.demoglayer}`;
        wmslayerids.push(demoglayerid);
      }

      if (control.isDensityOverlayActive() && !control.isInspectionFailureRateOverlayActive()) {
        wmslayerids.push(densitychoice.activeLayer);
      }

      if (control.isInspectionFailureRateOverlayActive() && !control.isDensityOverlayActive()) {
        wmslayerids.push(inspectionFailureRatechoice.activeLayer);
      }

      if (control.isProximityOverlayActive()) {
        const proximityLayerName = control._getProximityWmsLayerName(proximitychoice);
        if (proximityLayerName && proximityLayerName !== LAYER_IDS.RETAILER_POINTS) {
          wmslayerids.push(proximityLayerName);
        }

        if (proximitychoice.activeLayer !== LAYER_IDS.RETAILER_POINTS) {
          let referenceLayers = [];
          const currentConfig = control._getCurrentLayerConfig();

          if (currentConfig && currentConfig.place) {
            if (currentConfig.place === 'near_school' && proximityLayerName !== LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ) {
              referenceLayers = ['venue-v2-publicschool', 'venue-v2-privateschool'];
            } else if (currentConfig.place === 'near_parks') {
              referenceLayers = ['venue-v2-park'];
            } else if (currentConfig.place === 'retailersnearparksschools') {
              referenceLayers = ['venue-v2-publicschool', 'venue-v2-privateschool', 'venue-v2-park'];
            }
          }
          if (proximityLayerName === LAYER_IDS.RETAILERS_NEAR_RETAILERS_AZ) {
            referenceLayers = [];
          }
          wmslayerids.push(...referenceLayers);
        }
      }

      if (control.options.RETAILERSETID === 0) {
        wmslayerids = wmslayerids.map((layerid) => RETAILER_FILTER_MAPPING[layerid] || layerid);
      }

      if (control.options.VENUESETID === 0) {
        wmslayerids = wmslayerids.map((layerid) => VENUE_FILTER_MAPPING[layerid] || layerid);
      }

      if (control.options.mapType === 'manage_assignments') {
        wmslayerids = wmslayerids.map((layerid) => {
          if (layerid === 'retailerpoints') {
            return 'companyassignments';
          }
          return layerid;
        });
      }

      wmslayerids = [...new Set(wmslayerids)];

      wmslayerids.sort((p, q) => {
        const pz = control.getLayerZIndex(p) || 0;
        const qz = control.getLayerZIndex(q) || 0;
        return pz - qz;
      });

      if (wmslayerids.length) {
        const params = {
          layers: wmslayerids.join(','),
          RETAILERSETID: control.options.RETAILERSETID,
          VENUESETID: control.options.VENUESETID,
          SUBDOMAIN: control.options.subdomain.toUpperCase(),
          CAMPAIGNID: control.options.campaignId,
          DEMOGPOLY:
            demogchoice && !control.isDensityOverlayActive() && !control.isInspectionFailureRateOverlayActive()
              ? demogchoice.polytype
              : '',
          POLYTYPE: '',
          CALCPOLYBOUNDSLUGS: (() => {
            let boundsSlugs = [];

            if (calcpolyboundschoices.length) {
              boundsSlugs = [...calcpolyboundschoices];
            }

            if (control.isInspectionFailureRateOverlayActive()) {
              boundsSlugs.push(inspectionFailureRatechoice.polytype);
            }

            const uniqueBoundsSlugs = [...new Set(boundsSlugs)];

            return uniqueBoundsSlugs.length ? uniqueBoundsSlugs.map((x) => `'${x}'`).join(',') : '';
          })(),
          CALCPOLYLABELSLUGS: calcpolylabelschoices.length ? calcpolylabelschoices.map((x) => `'${x}'`).join(',') : '',
        };

        if (control.isDensityOverlayActive()) {
          if (densitychoice.activeLayer === LAYER_IDS.RETAILERS_DENSITY) {
            params.POLYTYPE = densitychoice.densityPolytype;
          } else if (densitychoice.activeLayer === LAYER_IDS.RETAILERS_SWAMP) {
            params.POLYTYPE = densitychoice.swampPolytype;
          }
        }

        if (control.isInspectionFailureRateOverlayActive()) {
          params.POLYTYPE = inspectionFailureRatechoice.polytype;
          if (inspectionFailureRatechoice.agencyId) {
            params.AGENCYID = inspectionFailureRatechoice.agencyId;
          }
          if (inspectionFailureRatechoice.startDate) {
            params.VISITDATEMIN = moment(inspectionFailureRatechoice.startDate).format('YYYY-MM-DD');
          }
          if (inspectionFailureRatechoice.endDate) {
            params.VISITDATEMAX = moment(inspectionFailureRatechoice.endDate).format('YYYY-MM-DD');
          }
        }

        if (control.isProximityOverlayActive()) {
          const proximityDistance = control._getProximityDistanceParameter(proximitychoice);
          if (proximityDistance) {
            params.DISTANCE = proximityDistance;
            params.DISTANCE_METERS = PROXIMITY_DISTANCE_IN_METERS[proximityDistance] || '';
          } else {
            params.DISTANCE = '';
            params.DISTANCE_METERS = '';
          }
        } else {
          params.DISTANCE = '';
          params.DISTANCE_METERS = '';
        }

        Object.keys(params).forEach((key) => {
          if (params[key] === '' || params[key] === undefined || params[key] === null) {
            delete params[key];
          }
        });
        if (control.isInspectionFailureRateOverlayActive() && !inspectionFailureRatechoice.agencyId) {
          delete this._overlay.wmsParams.AGENCYID;
        }

        this._overlay.setParams(params);
        this._overlay.addTo(this._map);
      } else {
        this._overlay.remove();
      }

      control._wmssource.options.identifyLayers = wmslayerids.filter((layerid) =>
        IDENTIFIABLE_LAYERS.includes(layerid),
      );
    };

    this._wmssource.addSubLayer = function (name, donotredraw) {
      this._subLayers[name] = true;
      if (!donotredraw) this.refreshOverlay();
    };

    this._wmssource.removeSubLayer = function (name, donotredraw) {
      delete this._subLayers[name];
      if (!donotredraw) this.refreshOverlay();
    };
  },

  handleWmsGetFeatureInfoResults(latlng, xmlstring) {
    let featureInfo = 'No features found';

    if (xmlstring && xmlstring !== 'error' && xmlstring.indexOf('<gml:featureMember>') !== -1) {
      try {
        const featureXml = xmlstring.split('<gml:featureMember>')[1].split('</gml:featureMember>')[0];
        const nameMatch = featureXml.match(/<NAME>(.*?)<\/NAME>/i);
        const addressMatch = featureXml.match(/<ADDRESS>(.*?)<\/ADDRESS>/i);

        if (nameMatch && nameMatch[1]) {
          featureInfo = `<strong>${nameMatch[1]}</strong>`;
          if (addressMatch && addressMatch[1]) {
            featureInfo += `<br>${addressMatch[1]}`;
          }
        }
      } catch (e) {
        // Error parsing feature info - keep default message
      }
    }

    L.popup().setLatLng(latlng).setContent(featureInfo).openOn(this._map);
  },

  setupMapClickHandling() {
    const control = this;

    this._map.on('click', function (e) {
      // Check if the original DOM event target is an input/select element
      const originalEvent = e?.originalEvent;
      if (originalEvent) {
        const target = originalEvent?.target;

        // Skip if clicking on interactive elements
        if (
          target.closest('.BaseSelect__control') ||
          target.closest('.BaseSelect__menu') ||
          target.closest('.BaseSelect__option') ||
          target.matches('input, select, button, textarea') ||
          target.closest('[role="combobox"]') ||
          target.closest('[role="option"]')
        ) {
          return; // Don't process map click
        }
      }

      if (
        control._wmssource &&
        control._wmssource.options.identifyLayers &&
        control._wmssource.options.identifyLayers.length > 0
      ) {
        const point = this.latLngToContainerPoint(e.latlng, this.getZoom());
        control._wmssource.getFeatureInfo(
          point,
          e.latlng,
          control._wmssource.options.identifyLayers,
          function (latlng, info) {
            control.handleWmsGetFeatureInfoResults(latlng, info);
          },
        );
      }
    });
  },

  _forceRefresh() {
    if (this._refreshTimeout) {
      clearTimeout(this._refreshTimeout);
      this._refreshTimeout = null;
    }

    if (this._wmssource) {
      this._wmssource.refreshOverlay();
    }

    this._renderReactComponent();
  },

  refresh() {
    if (this._refreshTimeout) {
      clearTimeout(this._refreshTimeout);
    }

    this._refreshTimeout = setTimeout(() => {
      if (this._wmssource) {
        this._wmssource.refreshOverlay();
      }

      this._updateUIState();
      this._refreshTimeout = null;
    }, 50);
  },

  onRemove(map) {
    if (this._container) {
      ReactDOM.unmountComponentAtNode(this._container);
    }

    if (this._wmssource) {
      this._wmssource.remove();
    }

    L.Control.prototype.onRemove.call(this, map);
  },
});
